import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getTours } from '../services/api';
import { MapPin, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { tourCurrencySymbol } from '@/lib/utils';
import { TourDateBadge } from '../components/TourDateBadge';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';

export default function YurticiToursPage() {
  const { language } = useLanguage();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTours = async () => {
      try {
        const response = await getTours({ tour_type: 'yurtici', lang: language });
        setTours(response.data);
      } catch (error) {
        console.error('Error fetching tours:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTours();
  }, [language]);

  return (
    <div className="bg-gray-50 min-h-screen">
      <SEO title="Yurtiçi Turlar" description="Kapadokya, Ege, Karadeniz ve Doğu Anadolu konaklamalı yurtiçi turları. Güncel tarihler, oda tipine göre fiyatlar ve online rezervasyon." />
      {/* Header */}
      <section className="bg-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="yurtici-tours-title">
            Yurtiçi Turlar
          </h1>
          <p className="text-lg text-gray-300">
            Türkiye'nin eşsiz güzelliklerini keşfedin
          </p>
        </div>
      </section>

      {/* Tours Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
                  <div className="h-64 bg-gray-200"></div>
                  <div className="p-6">
                    <div className="h-6 bg-gray-200 rounded mb-4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : tours.length === 0 ? (
            <div className="text-center py-20">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Yakında Yurtiçi Turlarımız!</h2>
                <p className="text-lg text-gray-600 mb-6">
                  Kapadokya, Efes, Pamukkale ve daha fazlası için hazırlık yapıyoruz.
                </p>
                <Link to="/turlar/istanbul">
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full font-ui px-8">
                    İstanbul Turlarına Göz At
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {tours.map((tour) => (
                <Link
                  to={`/turlar/${tour.slug}`}
                  key={tour.id}
                  className="group bg-white rounded-2xl overflow-hidden border border-gray-100 transition-all hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="relative overflow-hidden h-64">
                    <img
                      src={tour.image_url || 'https://images.pexels.com/photos/3264723/pexels-photo-3264723.jpeg?w=800'}
                      alt={tour.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://images.pexels.com/photos/3264723/pexels-photo-3264723.jpeg?w=800';
                      }}
                    />
                    <TourDateBadge tour={tour} />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center space-x-2 text-sm text-gray-500 mb-3">
                      <MapPin size={16} />
                      <span>{tour.category}</span>
                      <span className="text-gray-300">•</span>
                      <Clock size={16} />
                      <span>{tour.duration}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{tour.title}</h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{tour.short_description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold font-ui text-orange-500">{tourCurrencySymbol(tour)}{tour.price}</span>
                      <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full font-ui">
                        Detay
                      </Button>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}